import { ACCESS_TOKEN_COOKIE_NAME } from 'stash/constants/session';
import { AI_BASE_URL } from 'stash/constants';

import { getCookie } from 'stash/utils';
import {
	AGENT_MESSAGE,
	ChatAnalytics,
	isAgentMessage,
	isAgentResponseChunk,
	isAgentResponseEnd,
	isAgentResponseStart,
	isTopicSuggestionsData,
	Message,
	TrackFunction,
	USER_MESSAGE,
} from './types';
import { v1_AiChat_Failed } from '@stashinvest/product-events-javascript/lib/v1/event_static_methods';

/**
 * Determines if we show the load indicator/spinner while waiting for a chat message.
 */
export const getLoading = (
	message: Message,
	length: number,
	index: number,
	loading: boolean
) => {
	const isLastMessage = index === length - 1;
	const lastMessageFromUser = isLastMessage && message.type === USER_MESSAGE;
	const waitingOnAgentChunk = isLastMessage && message.text.length === 0;
	return loading && (lastMessageFromUser || waitingOnAgentChunk);
};

export const scrollToBottom = (id: string) => {
	const element = document.getElementById(id);
	if (element) element.scrollTop = element.scrollHeight;
};

const CHAT_API_TIMEOUT_MS = 25000;
/**
 * The following is a helper function that posts a chat message to the AI model and returns an http stream.
 * It implements a timeout of n seconds on the stream in case the user loses wifi/cell signal or other misc network reasons that interrupt the stream.
 * The request is aborted and a fallback message is displayed to the user if the timeout is exceeded.
 * The timeout is cleared when we receive a message that has a text length > 0.
 * @param baseUrl The base url of the streaming api endpoint.
 * @param uuid The user's uuid.
 * @param input The user's chat input.
 * @param errorCallBack The error callback function when the stream request fails or times out.
 * @param focus The focus of the AI model.
 * @param newAccessToken The user's new access token, passed after refreshing the token.
 * @returns A promise containing the response and the timeout id.
 */
export const postChatMessage = async (
	uuid: string,
	input: string,
	screen: string,
	focus: string | null,
	newAccessToken: string | null,
	errorCallBack: () => void,
	timeout = CHAT_API_TIMEOUT_MS
): Promise<{ response: Response; timeoutId: NodeJS.Timeout }> => {
	const baseUrl = AI_BASE_URL;
	const controller = new AbortController();
	const signal = controller.signal;

	const timeoutId = setTimeout(() => {
		clearTimeout(timeoutId);
		console.error(`AI Error: Request timed out after ${CHAT_API_TIMEOUT_MS}ms.`);
		errorCallBack();
		controller.abort();
	}, timeout);

	try {
		const response = await fetch(`${baseUrl}/api/v1/onboarding/users/${uuid}/chat`, {
			signal,
			method: 'POST',
			headers: {
				Authorization: newAccessToken || getCookie(ACCESS_TOKEN_COOKIE_NAME) || '',
				'Content-Type': 'application/json',
			},
			body: JSON.stringify({
				body: input,
				context: {
					platform: 'web',
					screen,
				},
				focus,
				initiateConversation: false,
			}),
		});

		return { response, timeoutId };
	} catch (e) {
		clearTimeout(timeoutId);
		throw e;
	}
};

const FALLBACK_MESSAGE_TEXT = [
	"I'm sorry, can you please repeat your question.",
	"I'm sorry, I didn't understand that. Can you please rephrase your question?",
	"Apologies, I didn't catch that. What would you like to chat about?",
];

const ERROR_MESSAGE_TEXT = [
	"I'm on a coffee break. Even AI needs a caffeine boost sometimes! Be back soon! ☕️",
	"I'm sorry, I am having trouble connecting to the server. Please try again later.",
	"I'm currently out of the office recharging my circuits. Chat again soon!",
	"I'm on a quick maintenance break. Don't worry, I'll be back in a byte!",
	"I'm sorry, I'm out exploring the digital world. I'll be back before you can say 'artificial intelligence'!",
];

export const getRandomErrorMessage = () => {
	const randomIndex = Math.floor(Math.random() * ERROR_MESSAGE_TEXT.length);
	const errorMessageText = ERROR_MESSAGE_TEXT[randomIndex];
	return {
		id: crypto.randomUUID(),
		text: errorMessageText,
		type: AGENT_MESSAGE,
	};
};

export const getRandomFallbackMessage = () => {
	const randomIndex = Math.floor(Math.random() * FALLBACK_MESSAGE_TEXT.length);
	const fallbackText = FALLBACK_MESSAGE_TEXT[randomIndex];
	return {
		id: crypto.randomUUID(),
		text: fallbackText,
		type: AGENT_MESSAGE,
	};
};

export const getMessagesForErrorScenario = (prev: Message[]) => {
	const fallbackMessage = getRandomFallbackMessage();
	if (prev.length === 0) return [fallbackMessage];

	return prev.map((message: Message, index: number) => {
		if (index === prev.length - 1 && prev[prev.length - 1].type === AGENT_MESSAGE) {
			return {
				...message,
				text: message.text + ' ' + fallbackMessage.text,
			};
		}
		return message;
	});
};

export const resetUserInterfaceAfterError = (
	setLoading: React.Dispatch<React.SetStateAction<boolean>>,
	setTopics: React.Dispatch<React.SetStateAction<string[]>>,
	setMessages: React.Dispatch<React.SetStateAction<Message[]>>
) => {
	setLoading(false);
	setTopics([]);
	setMessages((prev: Message[]) => getMessagesForErrorScenario(prev));
};

/**
 * Reset UI and display a fallback message because the fetch has failed.
 */
export const fetchErrorHandler = (
	setLoading: React.Dispatch<React.SetStateAction<boolean>>,
	setTopics: React.Dispatch<React.SetStateAction<string[]>>,
	setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
	track: TrackFunction,
	analytics: ChatAnalytics
) => {
	resetUserInterfaceAfterError(setLoading, setTopics, setMessages);
	track(
		v1_AiChat_Failed({
			...analytics,
			error: `Request timed out after ${CHAT_API_TIMEOUT_MS}ms.`,
		})
	);
};

/**
 * Parses the http stream to messages and topics.
 * @param response The http stream response.
 * @param timeoutId The timeoutId of the stream request.
 * @param setLoading The loading state setter.
 * @param setMessages The messages state setter.
 * @param setTopics The topics state setter.
 */
export const parseResponse = async (
	response: Response,
	timeoutId: NodeJS.Timeout,
	setLoading: React.Dispatch<React.SetStateAction<boolean>>,
	setMessages: React.Dispatch<React.SetStateAction<Message[]>>,
	setTopics: React.Dispatch<React.SetStateAction<string[]>>,
	track: TrackFunction,
	analytics: ChatAnalytics
) => {
	const reader = response.body?.getReader();
	if (!reader) throw new Error('AI no reader available');

	// Read the streaming response
	while (true) {
		const { done, value } = await reader.read();
		if (done) {
			setLoading(false);
			break;
		}

		// Convert the chunk to text
		const chunk = new TextDecoder().decode(value);

		// Split into lines and parse JSON
		const lines = chunk.split('\n').filter((line) => line.trim());

		for (const line of lines) {
			try {
				const data = JSON.parse(line);
				if (isAgentResponseStart(data)) {
					// Add new agent message
					setMessages((prev) => [
						...prev,
						{ id: data.id, text: '', type: AGENT_MESSAGE },
					]);
				} else if (isAgentResponseChunk(data)) {
					setMessages((prev) =>
						prev.map((message) => {
							// Appends new streaming text to existing agent message using the id
							if (isAgentMessage(message) && message.id === data.id) {
								clearTimeout(timeoutId);
								return {
									id: message.id,
									type: AGENT_MESSAGE,
									text: message.text + data.body,
									interactionId: message.interactionId,
								};
							}
							return message;
						})
					);
				} else if (isAgentResponseEnd(data)) {
					if (isTopicSuggestionsData(data.data)) {
						setTopics(data.data.topics);
					}
				} else {
					console.warn('AI unknown response type:', data);
				}
			} catch (e) {
				const message = `AI error parsing JSON: ${line}`;
				console.error(message, e);
				resetUserInterfaceAfterError(setLoading, setTopics, setMessages);
				track(
					v1_AiChat_Failed({
						...analytics,
						error: message,
					})
				);
			}
		}
	}
};
